import { useCallback, useEffect, useState } from "react";
import { GiHoneycomb } from "react-icons/gi";
import { LuCopyPlus } from "react-icons/lu";
import { useNavigate } from "react-router-dom";
import AjudaTela from "../../components/AjudaTela";
import Layout from "../../components/Layout";
import ModalCadastroFazenda from "../../components/modais/ModalCadastroFazenda";
import { repositorioFazenda, repositorioFuncionario, repositorioTalhao } from "../../sync/repositories/fazendas";

const AJUDA = {
  titulo: "Cadastro de Talhões",
  descricao:
    "Talhão é uma divisão de área dentro de uma Fazenda (um lote de plantio, por exemplo). Esse cadastro é usado depois nos Plantios e nas Atividades para indicar em qual área do campo o trabalho foi realizado, além de registrar o histórico de cultura, solo e produtividade de cada área.",
  secoes: [
    {
      titulo: "Dados do Talhão",
      campos: [
        {
          nome: "Nome ou Código do Talhão",
          obrigatorio: true,
          explicacao:
            "Identificação curta do talhão, como é usada no dia a dia da fazenda (ex.: 'Talhão A', 'Norte 01').",
        },
        {
          nome: "Fazenda",
          obrigatorio: true,
          explicacao:
            "Fazenda à qual esse talhão pertence. Também define quais funcionários aparecem como opção no campo Responsável.",
        },
        {
          nome: "Área (hectares)",
          obrigatorio: true,
          explicacao:
            "Tamanho do talhão em hectares. É usado depois em cálculos de produtividade e de custo por área nas Atividades.",
        },
        {
          nome: "Localização",
          obrigatorio: false,
          explicacao:
            "Referência de onde o talhão fica dentro da fazenda (ex.: 'Região Norte', 'Próximo ao rio'), apenas para facilitar a identificação no campo.",
        },
        {
          nome: "Situação",
          obrigatorio: false,
          explicacao:
            "Indica se o talhão está Ativo ou Inativo. Talhões inativos continuam no histórico, mas sinalizam uma área fora de uso.",
        },
        {
          nome: "Responsável",
          obrigatorio: false,
          explicacao:
            "Funcionário responsável pelo talhão. A lista só é liberada depois de selecionar a Fazenda, pois mostra apenas funcionários daquela fazenda.",
        },
        {
          nome: "Cultura Atual / Cultura Anterior",
          obrigatorio: false,
          explicacao:
            "Registro de qual cultura está plantada agora e qual foi a anterior, útil para planejar rotação de culturas.",
        },
        {
          nome: "Tipo de Solo",
          obrigatorio: false,
          explicacao: "Classificação do solo do talhão (ex.: Argiloso, Arenoso), como referência para manejo.",
        },
        {
          nome: "Data de Plantio / Data de Colheita",
          obrigatorio: false,
          explicacao: "Datas de referência do ciclo atual do talhão.",
        },
        {
          nome: "Produtividade",
          obrigatorio: false,
          explicacao: "Produtividade obtida na última colheita, em kg/ha, para acompanhamento histórico.",
        },
        {
          nome: "Observações",
          obrigatorio: false,
          explicacao: "Espaço livre para anotações sobre o talhão.",
        },
      ],
    },
  ],
  dicas: [
    "Excluir um talhão com registros vinculados (como plantios ou atividades) não é permitido — verifique se não há vínculos antes de tentar excluir.",
    "O campo Responsável só mostra opções depois que uma Fazenda é selecionada, porque a lista de funcionários é filtrada pela fazenda escolhida.",
  ],
};

export default function GestaoTalhoes() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [carregandoLista, setCarregandoLista] = useState(true);
  const [talhoes, setTalhoes] = useState([]);
  const [talhoesFiltrados, setTalhoesFiltrados] = useState([]);
  const [editando, setEditando] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [mostrarModalFazenda, setMostrarModalFazenda] = useState(false);
  
  // Dados para os selects
  const [fazendas, setFazendas] = useState([]);
  const [funcionarios, setFuncionarios] = useState([]);
  
  const [filtros, setFiltros] = useState({
    busca: "",
    situacao: "todos"
  });
  
  const [formData, setFormData] = useState({
    nome_codigo: "",
    localizacao: "",
    hectares: "",
    cultura_atual: "",
    cultura_anterior: "",
    tipo_solo: "",
    data_plantio: "",
    data_colheita: "",
    produtividade: "",
    responsavel: "",
    situacao: "ativo",
    observacao: "",
    fazenda: "",
  });

  // Carregar fazendas
  const carregarFazendas = async () => {
    try {
      const response = await repositorioFazenda.listar();
      const dados = response.data.results || response.data;
      setFazendas(dados);
    } catch (error) {
      console.error("Erro ao carregar fazendas:", error);
    }
  };

  // Carregar funcionários por fazenda
  const carregarFuncionarios = useCallback(async (fazendaId) => {
    if (!fazendaId) {
      setFuncionarios([]);
      return;
    }

    try {
      const response = await repositorioFuncionario.listar(`?fazenda=${fazendaId}`);
      const dados = response.data.results || response.data;
      setFuncionarios(Array.isArray(dados) ? dados : []);
    } catch (error) {
      console.error("Erro ao carregar funcionários:", error);
      setFuncionarios([]);
    }
  }, []);

  // Carregar talhões
  const carregarTalhoes = useCallback(async () => {
    try {
      setCarregandoLista(true);
      const response = await repositorioTalhao.listar();
      const dados = response.data.results || response.data;
      setTalhoes(dados);
      setTalhoesFiltrados(dados);
    } catch (error) {
      console.error("Erro ao carregar talhões:", error);
      alert("Erro ao carregar lista de talhões");
    } finally {
      setCarregandoLista(false);
    }
  }, []);

  useEffect(() => {
    carregarTalhoes();
    carregarFazendas();
  }, [carregarTalhoes]);

  // Carregar funcionários quando a fazenda mudar
  useEffect(() => {
    carregarFuncionarios(formData.fazenda);
  }, [formData.fazenda, carregarFuncionarios]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validarFormulario = () => {
    if (!formData.nome_codigo.trim()) {
      alert("Informe o nome ou código do talhão.");
      return false;
    }
    if (!formData.fazenda) {
      alert("Selecione uma fazenda.");
      return false;
    }
    if (!formData.hectares || Number(formData.hectares) <= 0) {
      alert("Informe uma área válida em hectares.");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validarFormulario()) return;
    
    setLoading(true);

    const payload = {
      ...formData,
      hectares: Number(formData.hectares),
      produtividade: formData.produtividade ? Number(formData.produtividade) : null,
    };

    try {
      if (editando) {
        const response = await repositorioTalhao.atualizar(editandoId, payload);
        if (response.status === 200) {
          alert("Talhão atualizado com sucesso!");
          handleCancel();
          carregarTalhoes();
        }
      } else {
        const response = await repositorioTalhao.criar(payload);
        if (response.status === 201 || response.status === 200) {
          alert("Talhão cadastrado com sucesso!");
          handleCancel();
          carregarTalhoes();
        }
      }
    } catch (error) {
      console.error("Erro:", error);
      const data = error.response?.data;
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        const erros = Object.values(data).flat();
        alert(erros.join("\n"));
      } else if (Array.isArray(data)) {
        alert(data.join("\n"));
      } else {
        alert("Erro ao salvar. Verifique os dados e tente novamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditar = (talhao) => {
    setFormData({
      nome_codigo: talhao.nome_codigo || "",
      localizacao: talhao.localizacao || "",
      hectares: talhao.hectares || "",
      cultura_atual: talhao.cultura_atual || "",
      cultura_anterior: talhao.cultura_anterior || "",
      tipo_solo: talhao.tipo_solo || "",
      data_plantio: talhao.data_plantio || "",
      data_colheita: talhao.data_colheita || "",
      produtividade: talhao.produtividade || "",
      responsavel: talhao.responsavel?.id || talhao.responsavel || "",
      situacao: talhao.situacao || "ativo",
      observacao: talhao.observacao || "",
      fazenda: talhao.fazenda?.id || talhao.fazenda || "",
    });
    setEditando(true);
    setEditandoId(talhao.id);
    setMostrarFormulario(true);
    
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const handleNovoTalhao = () => {
    handleCancel();
    setMostrarFormulario(true);
  };

  const handleCancel = () => {
    setFormData({
      nome_codigo: "",
      localizacao: "",
      hectares: "",
      cultura_atual: "",
      cultura_anterior: "",
      tipo_solo: "",
      data_plantio: "",
      data_colheita: "",
      produtividade: "",
      responsavel: "",
      situacao: "ativo",
      observacao: "",
      fazenda: "",
    });
    setFuncionarios([]);
    setEditando(false);
    setEditandoId(null);
    setMostrarFormulario(false);
  };

  const handleExcluir = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir este talhão? Esta ação não pode ser desfeita.")) {
      return;
    }
    
    try {
      await repositorioTalhao.excluir(id);
      alert("Talhão excluído com sucesso!");
      carregarTalhoes();
      
      if (editandoId === id) {
        handleCancel();
      }
    } catch (error) {
      console.error("Erro ao excluir:", error);
      alert("Erro ao excluir talhão. Verifique se não há registros vinculados.");
    }
  };

  // Aplicar filtros
  const aplicarFiltros = () => {
    let filtrados = [...talhoes];
    
    if (filtros.busca) {
      const buscaLower = filtros.busca.toLowerCase();
      filtrados = filtrados.filter(t => 
        t.nome_codigo?.toLowerCase().includes(buscaLower) ||
        t.fazenda_nome?.toLowerCase().includes(buscaLower) ||
        t.cultura_atual?.toLowerCase().includes(buscaLower) ||
        t.localizacao?.toLowerCase().includes(buscaLower)
      );
    }
    
    if (filtros.situacao !== "todos") {
      filtrados = filtrados.filter(t => t.situacao === filtros.situacao);
    }
    
    setTalhoesFiltrados(filtrados);
  };

  const limparFiltros = () => {
    setFiltros({ busca: "", situacao: "todos" });
    setTalhoesFiltrados(talhoes);
  };

  // Formatar valores
  const formatarArea = (area) => {
    if (!area && area !== 0) return "—";
    return `${Number(area).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ha`;
  };

  const formatarProdutividade = (prod) => {
    if (!prod) return "—";
    return `${Number(prod).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} kg/ha`;
  };

  const formatarData = (data) => {
    if (!data) return "—";
    return new Date(data).toLocaleDateString('pt-BR', { timeZone: 'UTC' });
  };

  const getSituacaoInfo = (situacao) => {
    return situacao === "ativo" 
      ? { label: "Ativo", color: "bg-green-100 text-green-800" }
      : { label: "Inativo", color: "bg-gray-100 text-gray-800" };
  };

  return (
    <Layout>
      <div className="p-6 font-medium max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-2">
            <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2">Gestão de Talhões <GiHoneycomb size={38}/></h2>
            <AjudaTela {...AJUDA} />
          </div>
          {!mostrarFormulario && (
            <button
              onClick={handleNovoTalhao}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors flex items-center gap-2 shadow-md"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Novo Talhão
            </button>
          )}
        </div>
        
        {/* Formulário de Cadastro/Edição */}
        {mostrarFormulario && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-800">
                {editando ? "Editar Talhão" : "Cadastrar Novo Talhão"}
              </h3>
              <button
                onClick={handleCancel}
                className="text-gray-500 hover:text-gray-700"
                title="Fechar formulário"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="space-y-6">
                {/* Linha 1: Nome/Código, Fazenda, Hectares */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Nome ou Código do Talhão *</label>
                    <input
                      type="text"
                      name="nome_codigo"
                      value={formData.nome_codigo}
                      onChange={handleChange}
                      className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      placeholder="Ex: Talhão A, Norte 01, etc."
                      required
                    />
                  </div>

                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Fazenda *</label>
                    <div className="flex items-center gap-2">
                      <select
                        name="fazenda"
                        value={formData.fazenda}
                        onChange={handleChange}
                        className="flex-1 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        required
                      >
                        <option value="">Selecione uma fazenda</option>
                        {fazendas.map(f => (
                          <option key={f.id} value={f.id}>{f.nome}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => setMostrarModalFazenda(true)}
                        title="Cadastrar nova fazenda"
                        className="p-2 hover:bg-gray-200 rounded"
                      >
                        <LuCopyPlus size={26} className="text-green-600 hover:text-green-800" />
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Área (hectares) *</label>
                    <div className="flex items-center border border-green-500 rounded">
                      <input
                        type="number"
                        name="hectares"
                        value={formData.hectares}
                        onChange={handleChange}
                        className="w-full p-2 rounded-l focus:ring-2 focus:ring-green-300"
                        step="0.01"
                        min="0.01"
                        placeholder="0,00"
                        required
                      />
                      <span className="px-3 text-gray-700 bg-gray-100 rounded-r">ha</span>
                    </div>
                  </div>
                </div>

                {/* Linha 2: Localização, Situação, Responsável */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Localização</label>
                    <input
                      type="text"
                      name="localizacao"
                      value={formData.localizacao}
                      onChange={handleChange}
                      className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      placeholder="Ex: Região Norte, Próximo ao rio, etc."
                    />
                  </div>

                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Situação</label>
                    <select
                      name="situacao"
                      value={formData.situacao}
                      onChange={handleChange}
                      className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                    >
                      <option value="ativo">Ativo</option>
                      <option value="inativo">Inativo</option>
                    </select>
                  </div>

                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Responsável</label>
                    <select
                      name="responsavel"
                      value={formData.responsavel}
                      onChange={handleChange}
                      className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      disabled={!formData.fazenda}
                    >
                      <option value="">Selecione um responsável</option>
                      {funcionarios.map(f => (
                        <option key={f.id} value={f.id}>{f.nome}</option>
                      ))}
                    </select>
                    {!formData.fazenda && (
                      <span className="text-xs text-gray-500 mt-1">Selecione uma fazenda primeiro</span>
                    )}
                  </div>
                </div>

                {/* Linha 3: Cultura Atual, Cultura Anterior, Tipo Solo */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Cultura Atual</label>
                    <input
                      type="text"
                      name="cultura_atual"
                      value={formData.cultura_atual}
                      onChange={handleChange}
                      className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      placeholder="Ex: Soja, Milho, etc."
                    />
                  </div>

                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Cultura Anterior</label>
                    <input
                      type="text"
                      name="cultura_anterior"
                      value={formData.cultura_anterior}
                      onChange={handleChange}
                      className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      placeholder="Ex: Trigo, Pousio, etc."
                    />
                  </div>

                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Tipo de Solo</label>
                    <input
                      type="text"
                      name="tipo_solo"
                      value={formData.tipo_solo}
                      onChange={handleChange}
                      className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      placeholder="Ex: Argiloso, Arenoso, etc."
                    />
                  </div>
                </div>

                {/* Linha 4: Data Plantio, Data Colheita, Produtividade */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Data de Plantio</label>
                    <input
                      type="date"
                      name="data_plantio"
                      value={formData.data_plantio}
                      onChange={handleChange}
                      className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                    />
                  </div>

                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Data de Colheita</label>
                    <input
                      type="date"
                      name="data_colheita"
                      value={formData.data_colheita}
                      onChange={handleChange}
                      className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                    />
                  </div>

                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Produtividade</label>
                    <div className="flex items-center border border-green-500 rounded">
                      <input
                        type="number"
                        name="produtividade"
                        value={formData.produtividade}
                        onChange={handleChange}
                        className="w-full p-2 rounded-l focus:ring-2 focus:ring-green-300"
                        step="0.01"
                        min="0"
                        placeholder="0"
                      />
                      <span className="px-3 text-gray-700 bg-gray-100 rounded-r">kg/ha</span>
                    </div>
                  </div>
                </div>

                {/* Linha 5: Observações */}
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Observações</label>
                  <textarea
                    name="observacao"
                    value={formData.observacao}
                    onChange={handleChange}
                    rows={3}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                    placeholder="Informações adicionais sobre o talhão..."
                  />
                </div>

                {/* Botões do formulário */}
                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors disabled:bg-blue-300"
                  >
                    {loading ? "Salvando..." : (editando ? "Atualizar" : "Cadastrar")}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}
        
        {!mostrarFormulario && (
          <>
            {/* Seção de Filtros */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                Filtros de Busca
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Buscar por Nome, Fazenda ou Cultura</label>
                  <input
                    type="text"
                    placeholder="Digite o nome do talhão, fazenda ou cultura..."
                    value={filtros.busca}
                    onChange={(e) => setFiltros({...filtros, busca: e.target.value})}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                  />
                </div>
                
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Situação</label>
                  <select
                    value={filtros.situacao}
                    onChange={(e) => setFiltros({...filtros, situacao: e.target.value})}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                  >
                    <option value="todos">Todos os talhões</option>
                    <option value="ativo">Ativos</option>
                    <option value="inativo">Inativos</option>
                  </select>
                </div>
              </div>
              
              <div className="flex gap-3 mt-4">
                <button
                  onClick={aplicarFiltros}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition-colors"
                >
                  Aplicar Filtros
                </button>
                <button
                  onClick={limparFiltros}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded transition-colors"
                >
                  Limpar Filtros
                </button>
              </div>
            </div>

            {/* Tabela de Talhões */}
            <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-200 bg-gray-50">
                <p className="text-gray-600">
                  {talhoesFiltrados.length} talhão(ões) encontrado(s)
                </p>
              </div>
              
              {carregandoLista ? (
                <div className="p-8 text-center text-gray-500">Carregando...</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Código</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Nome</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Fazenda</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Área</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Cultura Atual</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Situação</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Responsável</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {talhoesFiltrados.map((talhao) => {
                        const situacaoInfo = getSituacaoInfo(talhao.situacao);
                        return (
                          <tr key={talhao.id} className="border-t border-gray-200 hover:bg-gray-50 transition-colors">
                            <td className="p-3 text-sm font-mono">{talhao.id}</td>
                            <td className="p-3 text-sm font-medium">{talhao.nome_codigo}</td>
                            <td className="p-3 text-sm">{talhao.fazenda_nome || "-"}</td>
                            <td className="p-3 text-sm">{formatarArea(talhao.hectares)}</td>
                            <td className="p-3 text-sm">{talhao.cultura_atual || "—"}</td>
                            <td className="p-3">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${situacaoInfo.color}`}>
                                {situacaoInfo.label}
                              </span>
                            </td>
                            <td className="p-3 text-sm">{talhao.responsavel_nome || "—"}</td>
                            <td className="p-3">
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleEditar(talhao)}
                                  className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded text-sm transition-colors"
                                  title="Editar"
                                >
                                  Editar
                                </button>
                                <button
                                  onClick={() => handleExcluir(talhao.id)}
                                  className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm transition-colors"
                                  title="Excluir"
                                >
                                  Excluir
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      
                      {talhoesFiltrados.length === 0 && (
                        <tr>
                          <td colSpan="8" className="p-8 text-center text-gray-500">
                            {filtros.busca || filtros.situacao !== "todos"
                              ? "Nenhum talhão encontrado com os filtros selecionados."
                              : "Nenhum talhão cadastrado."}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
      <ModalCadastroFazenda
        isOpen={mostrarModalFazenda}
        onClose={() => setMostrarModalFazenda(false)}
        onSuccess={async (nova) => {
          await carregarFazendas();
          setFormData((prev) => ({ ...prev, fazenda: nova.id }));
          setMostrarModalFazenda(false);
        }}
      />
    </Layout>
  );
}